import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import type { DxfFile } from "@ando/core";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { CircleDot, Compass, Shirt } from "lucide-react";
import {
  cadFormatBadgeFor,
  extOf,
  formatBytes,
  resolveCadFormat,
  statusMetaFor,
} from "../utils";

/**
 * DxfFileRow — single row in the DXF library list.
 *
 * Layout mirrors ``technical.tsx`` so the visual language stays consistent.
 * Status chip vocabulary + byte/ext formatters live in ``../utils`` —
 * shared with ``DxfPreviewStage`` so the row chip and the canvas-strip
 * chip can never drift apart (June 2026 DRY audit fix).
 */

export interface DxfFileRowProps {
  readonly file: DxfFile;
  readonly isSelected?: boolean;
  readonly onSelect?: (file: DxfFile) => void;
}

export function DxfFileRow({ file, isSelected, onSelect }: DxfFileRowProps) {
  const { i18n } = useLingui();
  const meta = statusMetaFor(i18n, file);
  const time = formatRelativeTime(file.updated_at ?? file.created_at);
  // Fashion-axis rows (garment mesh / CLO project / apparel pattern) get a
  // friendly accent badge + a garment glyph. ``formatBadge`` is null for
  // every mechanical format, so DXF/STEP/… rows render byte-identically.
  const formatBadge = cadFormatBadgeFor(i18n, resolveCadFormat(file));
  const isFashion = formatBadge !== null;

  // Active state reads through typography (font-semibold + ink-1 filename)
  // and the always-orange glyph — the pencil nav ladder, not a marketing
  // fill or left bar. Selected row gets a subtle bg-card lift over the
  // rail's cream panel; unselected is bare.
  return (
    <button
      type="button"
      onClick={() => onSelect?.(file)}
      aria-pressed={isSelected}
      className={`flex w-full items-center gap-3 rounded-lg border border-hairline px-3 py-2.5 text-left transition-all hover:translate-x-[2px] hover:border-ando-orange/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream motion-reduce:transition-none ${
        isSelected ? "bg-card" : "bg-transparent"
      }`}
    >
      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ando-orange-soft text-ando-orange"
      >
        {isFashion ? (
          <Shirt size={16} strokeWidth={1.75} />
        ) : (
          <Compass size={16} strokeWidth={1.75} />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-[13px] text-ink-1 ${
            isSelected ? "font-semibold" : "font-medium"
          }`}
        >
          {file.filename}
        </span>
        <span className="mt-0.5 block text-[10px] text-ink-3 tabular-nums">
          {extOf(file.filename)} · {formatBytes(file.file_size)}
          {time ? <> · {time}</> : null}
          {file.category ? <> · {file.category}</> : null}
        </span>
      </span>
      {/* Topology facet — searchable inline chip when the BE topology
          analyzer counted ≥1 cylindrical hole. Renders as "○ 4" with a
          ring icon (matches the SOTA UX recommendation to surface
          features as discoverable facets, not buried in prose).
          Hidden for files without holes (most 2D DXF / mesh STL). */}
      {(file.holes_count ?? 0) > 0 ? (
        <span
          className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-ink-4/15 px-1.5 py-0.5 font-medium text-[10px] text-ink-2 tabular-nums"
          title={i18n._(
            plural(file.holes_count ?? 0, {
              one: "# foro cilindrico rilevato",
              other: "# fori cilindrici rilevati",
            }),
          )}
          aria-label={i18n._(
            plural(file.holes_count ?? 0, {
              one: "# foro",
              other: "# fori",
            }),
          )}
        >
          <CircleDot size={10} strokeWidth={2} aria-hidden />
          {file.holes_count}
        </span>
      ) : null}
      {formatBadge ? (
        <span
          className={`shrink-0 rounded-md px-1.5 py-0.5 font-medium text-[10px] ${formatBadge.className}`}
        >
          {formatBadge.label}
        </span>
      ) : null}
      <span
        className={`shrink-0 rounded-md px-2 py-0.5 font-medium text-[10px] tabular-nums ${meta.className}`}
      >
        {meta.label}
      </span>
    </button>
  );
}
DxfFileRow.displayName = "DxfFileRow";
